import { Button, Dialog, DialogContent, Typography, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentRef } from "src/models/documents";
import { DocumentFilesApiService, saveDocuments } from "src/services/documents";
import { v4 as uuid } from "uuid";
import { Props, State } from "./interfaces";
import { SaveResponse } from "../../models/api/save";
import { IncomingMailStatusRef } from "../../models/incoming-mail-status/incoming-mail-status-ref";
import { OutgoingMailStatusRef } from "../../models/outgoing-mail-status/outgoing-mail-status-ref";
import { IncomingMailApiService } from "../../services/incoming-mail/incoming-mail-api.service";
import { OutgoingMailApiService } from "../../services/outgoing-mail/outgoing-mail-api.service";
import { INCOMING_MAIL_STATUSES, OUTGOING_MAIL_STATUSES } from "../../utils/constants/constants";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import { handleUploadNewFiles } from "../Document/Document/utils";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const CopyMail = ({ outgoingMail, incomingMail, handleNotifyParent }: Props) => {
  const tRP = "components.copy_mail";
  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      CCDOpen: false,
      error: "",
      copiedOutgoingDocument: null,
      copiedIncomingDocument: null,
      copiedProcessedDocument: null,
      copiedSendingDocument: null,
    }),
    []
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleCopyMail = useCallback(
    async (
      copiedDocument: DocumentRef | null,
      copiedProcessedDocument?: DocumentRef | null,
      copiedSendingDocument?: DocumentRef | null
    ) => {
      setState((state) => ({ ...state, loading: true }));
      try {
        let response: SaveResponse | null = null;
        if (incomingMail) {
          response = await new IncomingMailApiService().saveItems([
            {
              ...incomingMail,
              id: uuid(),
              status: INCOMING_MAIL_STATUSES.Pending as IncomingMailStatusRef,
              document: copiedDocument,
              doNotProcessByOcr: false,
            },
          ]);
        } else if (outgoingMail) {
          response = await new OutgoingMailApiService().saveItems([
            {
              ...outgoingMail,
              id: uuid(),
              status: OUTGOING_MAIL_STATUSES.Pending as OutgoingMailStatusRef,
              document: copiedDocument,
              processedDocument: copiedProcessedDocument ?? null,
              sendingDocument: copiedSendingDocument ?? null,
              doNotProcessByOcr: false,
            },
          ]);
        }

        if (response?.error) {
          setState((state) => ({ ...state, error: response?.error ?? "" }));
        } else {
          setState({ ...INITIAL_STATE });
          handleNotifyParent();
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [INITIAL_STATE, handleNotifyParent, incomingMail, outgoingMail]
  );

  const handleCopyFiles = useCallback(
    async (
      copiedDocument: DocumentRef | null,
      copiedProcessedDocument: DocumentRef | null,
      copiedSendingDocument: DocumentRef | null
    ) => {
      const originalDocument = incomingMail?.document ?? outgoingMail?.document;
      const originalProcessedDocument = outgoingMail?.processedDocument;
      const originalSendingDocument = outgoingMail?.sendingDocument;

      try {
        setState((state) => ({ ...state, loading: true }));

        if (copiedDocument && originalDocument) {
          const contentToSave = await new DocumentFilesApiService().loadBlob(
            originalDocument.files[0].id,
            undefined,
            "/GetGenericDocumentsFilesContents"
          );
          const invokerId = uuid();
          const correlationID = uuid();

          const copiedDocumentFileResponse = await handleUploadNewFiles(
            copiedDocument,
            [
              {
                file: new File([contentToSave.data], originalDocument.files[0].name),
                fileName: originalDocument.files[0].name,
              },
            ],
            invokerId,
            correlationID,
            true,
            true
          );

          if (copiedDocumentFileResponse?.error) {
            setState((state) => ({ ...state, error: copiedDocumentFileResponse.error }));
            return;
          }
        }

        if (copiedProcessedDocument && originalProcessedDocument) {
          const contentToSave = await new DocumentFilesApiService().loadBlob(
            originalProcessedDocument.files[0].id,
            undefined,
            "/GetGenericDocumentsFilesContents"
          );
          const invokerId = uuid();
          const correlationID = uuid();

          const copiedProcessedDocumentFileResponse = await handleUploadNewFiles(
            copiedProcessedDocument,
            [
              {
                file: new File([contentToSave.data], originalProcessedDocument.files[0].name),
                fileName: originalProcessedDocument.files[0].name,
              },
            ],
            invokerId,
            correlationID,
            true,
            true
          );

          if (copiedProcessedDocumentFileResponse?.error) {
            setState((state) => ({ ...state, error: copiedProcessedDocumentFileResponse.error }));
            return;
          }
        }
        if (copiedSendingDocument && originalSendingDocument) {
          const contentToSave = await new DocumentFilesApiService().loadBlob(
            originalSendingDocument.files[0].id,
            undefined,
            "/GetGenericDocumentsFilesContents"
          );
          const invokerId = uuid();
          const correlationID = uuid();

          const copiedSendingDocumentFileResponse = await handleUploadNewFiles(
            copiedSendingDocument,
            [
              {
                file: new File([contentToSave.data], originalSendingDocument.files[0].name),
                fileName: originalSendingDocument.files[0].name,
              },
            ],
            invokerId,
            correlationID,
            true,
            true
          );

          if (copiedSendingDocumentFileResponse?.error) {
            setState((state) => ({ ...state, error: copiedSendingDocumentFileResponse.error }));
            return;
          }
        }

        handleCopyMail(copiedDocument, copiedProcessedDocument, copiedSendingDocument);
      } catch (error) {
        console.log(error);
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [handleCopyMail]
  );

  const handleCopyDocument = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    const allDocumentsToSave: DocumentRef[] = [];

    let documentToSave: DocumentRef | null = null;
    let processedDocumentToSave: DocumentRef | null = null;
    let sendingDocumentToSave: DocumentRef | null = null;

    if (incomingMail) {
      documentToSave = incomingMail.document ? { ...incomingMail.document, id: uuid(), files: [] } : null;

      documentToSave && allDocumentsToSave.push(documentToSave);
    }

    if (outgoingMail) {
      documentToSave = outgoingMail.document ? { ...outgoingMail.document, id: uuid(), files: [] } : null;

      processedDocumentToSave = outgoingMail.processedDocument
        ? { ...outgoingMail.processedDocument, id: uuid(), files: [] }
        : null;

      sendingDocumentToSave = outgoingMail.sendingDocument
        ? { ...outgoingMail.sendingDocument, id: uuid(), files: [] }
        : null;

      documentToSave && allDocumentsToSave.push(documentToSave);
      processedDocumentToSave && allDocumentsToSave.push(processedDocumentToSave);
      sendingDocumentToSave && allDocumentsToSave.push(sendingDocumentToSave);
    }

    try {
      const response = await saveDocuments([...allDocumentsToSave], null, true);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        await handleCopyFiles(documentToSave, processedDocumentToSave, sendingDocumentToSave);

        // setState((state)=>({
        //   ...state ,
        //   copiedOutgoingDocument : outgoingMail ? documentToSave: null,
        //   copiedIncomingDocument : incomingMail ? documentToSave : null,
        //   copiedProcessedDocument : processedDocumentToSave,
        //   copiedSendingDocument : sendingDocumentToSave
        // }))
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [handleCopyFiles, incomingMail, outgoingMail]);

  const handleCopy = useCallback(() => {
    setState((state) => ({ ...state, loading: true }));
    try {
      handleCopyDocument();
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [handleCopyDocument, handleCopyMail]);

  return (
    <>
      <Button
        onClick={() => setState((state) => ({ ...state, open: true }))}
        title={`${t(`${tRP}.button_title`)}`}
        style={{ color: theme.palette.text.primary }}
      >{`${t(`${tRP}.button_title`)}`}</Button>
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Typography>{`${t(`${tRP}.info`)}`}</Typography>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, open: false }))}
          handleSave={handleCopy}
          customSaveName={`${t(`${tRP}.yes_copy`)}`}
        />
      </Dialog>
    </>
  );
};

export default CopyMail;
